import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// VW Golf Mk1 GTI — a small 1970s hot hatch at true scale (1 unit = 1 metre):
// length 3.71, width 1.61, height 1.39, wheelbase 2.40, on 13-inch steel-look
// wheels. Nose is +Z. Giugiaro's folded-paper two-box shape: a boxy lower body,
// a boxy angular greenhouse with thin pillars and big glass, a short flat
// bonnet, an upright raked hatch with a wide C-pillar. GTI cues: round single
// headlamps in a full-width black grille panel framed by the signature red
// pinstripe, slim black bumpers with a deep front air dam, black sill and
// arch trim, a single left exhaust. Body panels carry the "paint" recolour
// slot (factory Mars Red); `drive` spins the wheels and `hazards` blinks the
// four indicators.

const PAINT = "#c22b1f"; // factory Mars Red — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (pillars, cowl, window surrounds)
const CLAD = "#26282c"; // matte black plastic (bumpers, sills, air dam)
const WELL = "#141518"; // wheel wells
const GLASS = "#2a3742"; // side and rear glazing
const SCREEN = "#3a4c5e"; // windscreen (a shade lighter)
const CHROME = "#ccd1d6";
const STEEL = "#9aa0a7"; // steel-wheel rim lip
const SPOKE = "#d7dbe0"; // bright wheel spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527"; // tail lamps
const STRIPE = "#ef3b2a"; // the GTI red grille pinstripe
const PLATE = "#eceff2";
const SLAT = "#3a3e44"; // grille slats
const DARK = "#101113"; // headlamp buckets, wheel faces

// Spec-sheet skeleton (metres). Wheels at the corners: very short overhangs.
const WHEEL_R = 0.28;
const WHEEL_X = 0.725; // half track
const AXLE_F = 1.12;
const AXLE_R = -1.28; // wheelbase 2.40

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One GTI steel wheel: tyre, brake disc, silver rim lip, dark face, four
 *  short bright spokes (the "football" pattern read), chrome centre cap.
 *  Every part shares `name` so the `drive` track spins them as one node.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.2, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.11, 0.11, 0.03, 12, BRAKE, [x - side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.175, 0.175, 0.045, 18, STEEL, [x + side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.15, 0.15, 0.03, 16, DARK, [x + side * 0.075, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 2; k++) {
    parts.push(
      box(0.05, 0.26, 0.055, SPOKE, [x + side * 0.08, y, z],
        [(k * Math.PI) / 2 + Math.PI / 4, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.03, 12, CHROME, [x + side * 0.095, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side plus a thin black
 *  arch lip — an upper half-torus over the tyre. The [0, pi/2, 0] yaw puts the
 *  torus in the YZ plane with its 0..pi arc sweeping exactly the top half. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.37, 0.37, 0.04, 18, WELL, [side * 0.785, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.35, 0.052, 6, 18, Math.PI),
      color: CLAD,
      position: [side * 0.8, WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits in the grille panel beside the round lamps; rear pair is the
// amber inner segment of each tail-lamp cluster.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.55, 0.375, 1.839], size: [0.12, 0.05, 0.016] },
  { name: "indicator-fr", pos: [0.55, 0.375, 1.839], size: [0.12, 0.05, 0.016] },
  { name: "indicator-rl", pos: [-0.6625, 0.78, -1.799], size: [0.105, 0.14, 0.022] },
  { name: "indicator-rr", pos: [0.6625, 0.78, -1.799], size: [0.105, 0.14, 0.022] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.04, 0.04, 1.44, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.4, 0.12, 3.1, TRIM, [0, 0.2, -0.05])); // underbody pan

  // -- Body masses (paint): the folded-paper lower box ------------------------
  P.push(paint(1.56, 0.6, 3.4, [0, 0.5, -0.1])); // main tub, sill to shoulder
  P.push(paint(1.56, 0.1, 2.35, [0, 0.85, -0.625])); // belt-line band, cowl to tail
  P.push(paint(1.46, 0.08, 1.25, [0, 0.785, 1.175], [0.064, 0, 0])); // flat bonnet, sunk below the fender line
  for (const s of S) P.push(paint(0.07, 0.08, 1.25, [s * 0.755, 0.825, 1.175], [0.064, 0, 0])); // fender tops
  P.push(paint(1.56, 0.57, 0.22, [0, 0.485, 1.71])); // nose block behind the grille panel
  P.push(paint(1.52, 0.06, 0.2, [0, 0.9, -1.7])); // hatch lower shelf under the rear glass

  // -- Black plastic: sills, slim bumpers, air dam -----------------------------
  for (const s of S) P.push(box(0.026, 0.13, 1.68, "#2b2e33", [s * 0.783, 0.275, -0.075])); // sill stripe, arch to arch
  P.push(box(1.58, 0.1, 0.05, CLAD, [0, 0.47, 1.835])); // slim front bumper
  P.push(box(1.58, 0.1, 0.05, CLAD, [0, 0.41, -1.82])); // slim rear bumper
  P.push(box(1.52, 0.16, 0.05, CLAD, [0, 0.28, -1.802])); // rear valance under the bumper
  P.push(box(1.57, 0.2, 0.05, CLAD, [0, 0.3, 1.805])); // GTI front air dam, full width

  // -- Greenhouse: boxy, thin pillars, big glass -------------------------------
  P.push(box(1.4, 0.03, 0.14, TRIM, [0, 0.878, 0.62])); // black cowl step over the sunk bonnet
  P.push(box(1.26, 0.5, 0.04, SCREEN, [0, 1.115, 0.44], [-0.48, 0, 0])); // upright-but-raked windscreen
  for (const s of S) P.push(paint(0.09, 0.54, 0.09, [s * 0.65, 1.115, 0.44], [-0.48, 0, 0])); // thin A-pillars
  for (const s of S) {
    // Side glass in two boxes whose front edges staircase along the screen
    // rake, so the painted pillar cover below reads as a sloped A-pillar from
    // the side instead of a wide vertical glass band.
    P.push(box(0.035, 0.44, 1.8, GLASS, [s * 0.705, 1.11, -0.6], [0, 0, s * 0.13])); // side glass band, inset
    P.push(box(0.035, 0.17, 0.17, GLASS, [s * 0.705, 0.975, 0.38], [0, 0, s * 0.13])); // door-glass front corner
    P.push(paint(0.05, 0.6, 0.16, [s * 0.73, 1.075, 0.456], [-0.48, 0, 0])); // A-pillar cover, proud of the glass
    P.push(box(0.024, 0.05, 0.12, TRIM, [s * 0.746, 0.905, 0.41])); // quarter-vent corner at the pillar base
    P.push(box(0.045, 0.44, 0.06, TRIM, [s * 0.71, 1.11, -0.75], [0, 0, s * 0.13])); // B-pillar, proud of the glass
    P.push(paint(0.09, 0.44, 0.34, [s * 0.71, 1.11, -1.39], [0, 0, s * 0.13])); // wide C-pillar, proud of the glass
    P.push(box(0.028, 0.03, 1.92, TRIM, [s * 0.764, 0.9, -0.54])); // black window-base surround
  }
  P.push(paint(1.42, 0.06, 1.78, [0, 1.355, -0.56])); // flat roof, tapered in from the fenders

  // -- Front fascia: black grille panel, round lamps, red pinstripe ------------
  P.push(box(1.44, 0.18, 0.03, TRIM, [0, 0.69, 1.815])); // full-width flat black grille panel
  for (const dy of [-0.05, 0, 0.05]) {
    P.push(box(0.72, 0.02, 0.012, SLAT, [0, 0.69 + dy, 1.831])); // horizontal slats between the lamps
  }
  for (const s of S) {
    P.push(cyl(0.095, 0.095, 0.016, 18, DARK, [s * 0.5, 0.69, 1.826], [Math.PI / 2, 0, 0])); // lamp bucket
    P.push(cyl(0.088, 0.088, 0.01, 18, CHROME, [s * 0.5, 0.69, 1.832], [Math.PI / 2, 0, 0])); // trim ring
    P.push(cyl(0.078, 0.078, 0.014, 18, LAMP, [s * 0.5, 0.69, 1.836], [Math.PI / 2, 0, 0])); // round headlamp
  }
  // The GTI signature: a thin red stripe framing the grille panel.
  P.push(box(1.46, 0.028, 0.014, STRIPE, [0, 0.775, 1.833])); // top rail
  P.push(box(1.46, 0.028, 0.014, STRIPE, [0, 0.59, 1.833])); // bottom rail
  for (const s of S) P.push(box(0.028, 0.213, 0.014, STRIPE, [s * 0.723, 0.6825, 1.833])); // side rails
  P.push(box(0.44, 0.09, 0.012, PLATE, [0, 0.47, 1.867])); // front plate on the bumper

  // -- Rear fascia: near-vertical hatch, big glass, simple tail lamps ----------
  P.push(box(1.28, 0.46, 0.04, GLASS, [0, 1.125, -1.545], [0.51, 0, 0])); // big hatch window
  for (const s of S) P.push(paint(0.12, 0.48, 0.06, [s * 0.63, 1.12, -1.55], [0.51, 0, 0])); // hatch pillars
  P.push(box(1.26, 0.03, 0.05, TRIM, [0, 0.935, -1.66])); // black hatch-glass base surround
  for (const s of S) {
    P.push(box(0.2, 0.14, 0.022, RED, [s * 0.51, 0.78, -1.799])); // rectangular tail lamp, near flush
  }
  P.push(box(0.5, 0.16, 0.012, TRIM, [0, 0.78, -1.803])); // plate recess between the lamps
  P.push(box(0.44, 0.12, 0.014, PLATE, [0, 0.78, -1.807])); // rear plate
  P.push(cyl(0.042, 0.042, 0.12, 10, "#3f4348", [-0.58, 0.235, -1.82], [Math.PI / 2, 0, 0])); // exhaust stub, left of the plate

  // -- Furniture ---------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.05, 0.022, 0.03, TRIM, [s * 0.795, 0.93, 0.44])); // short mirror arm
    P.push(box(0.07, 0.06, 0.035, TRIM, [s * 0.835, 0.93, 0.43])); // compact mirror head at the belt line
    P.push(box(0.045, 0.04, 0.012, CHROME, [s * 0.835, 0.93, 0.41])); // mirror glass
    P.push(box(0.016, 0.022, 0.12, CHROME, [s * 0.784, 0.83, -0.45])); // door handle (3-door)
    P.push(box(0.32, 0.012, 0.025, TRIM, [s * 0.24, 0.96, 0.55], [-0.48, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const vwGolfGti = defineModel({
  id: "vw-golf-gti",
  name: "VW Golf GTI Mk1",
  batchId: "cars",
  tags: ["car", "volkswagen", "golf", "gti", "hatchback", "hot-hatch", "classic"],
  summary: "A Mk1 VW Golf GTI hot hatch at true scale, in factory Mars Red.",
  description:
    "A VW Golf GTI Mk1 at real 1 unit = 1 metre scale (about 3.71 long, 1.61 " +
    "wide, 1.39 tall, 2.40 wheelbase — wheels at the corners). Giugiaro's " +
    "folded-paper two-box hatchback: short flat bonnet, upright raked " +
    "windscreen, flat roof, steep hatch with a big rear window and wide " +
    "C-pillars. GTI cues: round headlamps in a full-width black grille panel " +
    "framed by the signature red pinstripe, slim black bumpers with a deep " +
    "front air dam, black sill and arch trim, black window surrounds, " +
    "steel-look wheels with bright spokes, a single left exhaust. Body " +
    "panels carry the 'paint' recolour slot (factory Mars Red); `drive` " +
    "spins the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.0,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
